import { useState } from "react";
import { Settings2, Eye, EyeOff, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { TableColumn } from "./data-table";
import { useColumnVisibility } from "@/hooks/useColumnVisibility";
import { useCommonI18n } from "@/hooks/useModuleI18n";

interface ColumnVisibilityToggleProps {
  tableId: string;
  columns: TableColumn[];
  visibleColumns: string[];
  toggleColumn: (columnKey: string) => void;
  resetToDefaults: () => void;
  isColumnVisible: (columnKey: string) => boolean;
  onVisibilityChange?: (visibleColumns: TableColumn[]) => void;
  showLabel?: boolean; // Mostrar o ocultar el texto "Columnas"
}

export function ColumnVisibilityToggle({
  tableId,
  columns,
  visibleColumns,
  toggleColumn,
  resetToDefaults,
  isColumnVisible,
  onVisibilityChange,
  showLabel = true,
}: ColumnVisibilityToggleProps) {
  const { t } = useCommonI18n();
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleColumn = (columnKey: string) => {
    toggleColumn(columnKey);
    // La actualización se propagará automáticamente a través del estado compartido
  };

  const handleReset = () => {
    resetToDefaults();
    // La actualización se propagará automáticamente a través del estado compartido
  };

  const visibleCount = visibleColumns.length;
  const totalCount = columns.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings2 className="h-4 w-4" />
          {showLabel && t("table.columns.count", { visible: visibleCount, total: totalCount })}
          {!showLabel && (
            <span className="sr-only">
              {t("table.columns.count", { visible: visibleCount, total: totalCount })}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 z-[60]" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{t("table.columns.showColumns")}</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-8 px-2 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              {t("table.columns.reset")}
            </Button>
          </div>

          <Separator />

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {columns.map((column) => {
              const isVisible = isColumnVisible(column.key);
              const isRequired = column.required === true;

              return (
                <div
                  key={column.key}
                  className="flex items-center space-x-2 py-1"
                >
                  <Checkbox
                    id={`column-${column.key}`}
                    checked={isVisible}
                    onCheckedChange={() =>
                      !isRequired && handleToggleColumn(column.key)
                    }
                    disabled={isRequired}
                    className="h-4 w-4"
                  />
                  <div className="flex items-center flex-1 min-w-0">
                    {isVisible ? (
                      <Eye className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <EyeOff className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    )}
                    <Label
                      htmlFor={`column-${column.key}`}
                      className={`text-sm truncate cursor-pointer ${
                        isRequired ? "text-muted-foreground" : ""
                      }`}
                    >
                      {column.label}
                      {isRequired && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({t("table.columns.required")})
                        </span>
                      )}
                    </Label>
                  </div>
                </div>
              );
            })}
          </div>

          {visibleCount < totalCount && (
            <>
              <Separator />
              <div className="text-xs text-muted-foreground">
                {totalCount - visibleCount}{" "}
                {totalCount - visibleCount === 1
                  ? t("table.columns.hidden")
                  : t("table.columns.hiddenPlural")}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ColumnVisibilityToggle;
